import React, { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import TopicPicker, { type Topic } from '@components/TopicPicker/pc';
import PCFeedbackProblemPicker from '@components/feedbackProblemPicker/pc';
import { parseProblemCategories, type FeedbackProblemCategory, type FeedbackProblemItem } from '@components/feedbackProblemPicker';
import { readFeedbackProblems, readTopicCategoryList, readTopicList, readTopicsByIds } from '@server';
import { BASE_IMG_URL, threadConversionImage } from '@images/imageUrls';
import { releaseModalBodyScrollLock, syncModalBodyScrollLock } from '@utils/modalScrollLock';
import { MAX_FEEDBACK_IMAGES } from '../../pages/setFeedbackPost/constants/feedbackPostLimits';
import styles from './pc/index.module.scss';

export interface ThreadConversionPayload {
    threadId: string | number;
    sourceType: number;
    targetType: 'normal' | 'feedback';
    topicIds?: string[];
    problemId?: string;
}

interface ThreadConversionProps {
    visible: boolean;
    threadId: string | number;
    sourceType: number;
    initialTopics?: Topic[];
    initialProblemId?: string;
    imageCount?: number;
    hasVideo?: boolean;
    onClose: () => void;
    /** 由详情页提交转换接口，未完成时 reject。 */
    onConvert: (payload: ThreadConversionPayload) => Promise<void>;
    onSuccess?: (payload: ThreadConversionPayload) => void | Promise<void>;
}

type Step = 'topics' | 'problems' | 'confirm' | 'submitting';
interface Category { id: string; name: string; }
const EMPTY_TOPICS: Topic[] = [];
const topicSearch = { getTopicList: readTopicList };
const getList = (res: any): any[] => {
    const list = res?.data?.data ?? res?.data?.list ?? res?.data;
    return Array.isArray(list) ? list : [];
};
const formatTopic = (topic: any, category?: string): Topic => ({
    ...topic,
    id: String(topic.id),
    name: topic.name,
    cover: topic.cover || topic.avatar || `${BASE_IMG_URL}/common/official2.png`,
    count: topic.count || `${topic.postCount || 0}帖子 ${topic.discussionCount || 0}讨论量`,
    category: category || (typeof topic.category === 'string' ? topic.category : topic.category?.name),
});

/** 帖子互转流程。提交通过回调接入，选择器复用现有发布/反馈组件。 */
const ThreadConversion = ({ visible, threadId, sourceType, initialTopics = EMPTY_TOPICS, initialProblemId, imageCount = 0, hasVideo = false, onClose, onConvert, onSuccess }: ThreadConversionProps) => {
    const [step, setStep] = useState<Step>('topics');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [categories, setCategories] = useState<Category[]>([]);
    const [activeCategory, setActiveCategory] = useState('');
    const [topics, setTopics] = useState<Topic[]>([]);
    const [selectedTopics, setSelectedTopics] = useState<Topic[]>(EMPTY_TOPICS);
    const [problemCategories, setProblemCategories] = useState<FeedbackProblemCategory[]>([]);
    const [selectedProblem, setSelectedProblem] = useState<FeedbackProblemItem | null>(null);
    const [hasMore, setHasMore] = useState(false);
    const [loadingMore, setLoadingMore] = useState(false);
    const session = useRef(0);
    const topicRequest = useRef(0);
    const submitting = useRef(false);
    const panel = useRef<HTMLDivElement>(null);
    const callbacks = useRef({ onClose, onConvert, onSuccess });
    callbacks.current = { onClose, onConvert, onSuccess };
    const normalizedInitialTopics = useMemo(() => initialTopics.slice(0, 3).map(topic => formatTopic(topic)), [initialTopics]);
    const isFeedback = sourceType === 2;
    const confirmText = isFeedback
        ? '是否将该帖转为普通帖？'
        : hasVideo && imageCount > 0
            ? '当前帖子含图片和视频，转换为问题帖后只保留视频内容'
            : !hasVideo && imageCount > MAX_FEEDBACK_IMAGES
                ? `当前帖子图片较多，转换为问题帖后只保留前${MAX_FEEDBACK_IMAGES}张图片`
                : '是否将该帖转为问题反馈帖？';

    const dismiss = () => {
        if (!submitting.current) callbacks.current.onClose();
    };

    const loadTopics = async (category: Category, page: number, currentSession = session.current) => {
        const requestId = ++topicRequest.current;
        if (page === 1) {
            setLoading(true);
            setTopics([]);
        } else {
            setLoadingMore(true);
        }
        setError('');
        try {
            const res = await readTopicList({
                ...(category.id ? { 'filter[categoryId]': category.id } : {}),
                'page[number]': page,
                'page[size]': 20,
            });
            if (currentSession !== session.current || requestId !== topicRequest.current) return;
            if (res?.code !== 0) throw new Error('load failed');
            const nextTopics = getList(res).map(topic => formatTopic(topic, category.name));
            setTopics(previous => page === 1 ? nextTopics : [...previous, ...nextTopics.filter(topic => !previous.some(item => item.id === topic.id))]);
            // 将分类归属保存在已选项上，避免切换列表后角标失去依据。
            setSelectedTopics(previous => previous.map(topic => {
                const match = nextTopics.find(item => item.id === topic.id);
                return !topic.category && match ? { ...topic, category: match.category } : topic;
            }));
            setHasMore(Boolean(res?.data?.page?.hasMore));
        } catch {
            if (currentSession === session.current && requestId === topicRequest.current) setError('话题加载失败，请重试');
        } finally {
            if (currentSession === session.current && requestId === topicRequest.current) {
                setLoading(false);
                setLoadingMore(false);
            }
        }
    };

    const loadOptions = async (currentSession: number) => {
        setError('');
        setLoading(true);
        try {
            if (isFeedback) {
                const missingCategoryTopics = normalizedInitialTopics.filter(topic => !topic.category);
                const [res, selectedResponse] = await Promise.all([
                    readTopicCategoryList({ 'page[number]': 1, 'page[size]': 100 }),
                    missingCategoryTopics.length
                        ? readTopicsByIds({ filter: { id: missingCategoryTopics.map(topic => topic.id).join(',') } })
                        : Promise.resolve(null),
                ]);
                if (currentSession !== session.current) return;
                if (res?.code !== 0) throw new Error('load failed');
                if (missingCategoryTopics.length && selectedResponse?.code !== 0) throw new Error('load failed');
                const selectedDetails = getList(selectedResponse).map(topic => formatTopic(topic));
                setSelectedTopics(previous => previous.map(topic => {
                    const detail = selectedDetails.find(item => item.id === topic.id);
                    return detail?.category ? { ...topic, category: detail.category } : topic;
                }));
                const nextCategories: Category[] = getList(res).map(item => ({ id: String(item.id), name: item.name }));
                if (!nextCategories.length) nextCategories.push({ id: '', name: '全部' });
                setCategories(nextCategories);
                setActiveCategory(nextCategories[0].name);
                await loadTopics(nextCategories[0], 1, currentSession);
            } else {
                const res = await readFeedbackProblems();
                if (currentSession !== session.current) return;
                if (res?.code !== 0) throw new Error('load failed');
                const nextCategories = parseProblemCategories(res);
                setProblemCategories(nextCategories);
                setSelectedProblem(nextCategories.flatMap(category => category.problems).find(problem => problem.value === initialProblemId) || null);
                if (!nextCategories.length) setError('暂无可用的问题类型，请重试');
                setLoading(false);
            }
        } catch {
            if (currentSession === session.current) {
                setError(isFeedback ? '话题加载失败，请重试' : '问题类型加载失败，请重试');
                setLoading(false);
            }
        }
    };

    useEffect(() => {
        const currentSession = ++session.current;
        submitting.current = false;
        if (!visible) return;
        setStep(isFeedback ? 'topics' : 'problems');
        setSelectedTopics(normalizedInitialTopics);
        setSelectedProblem(null);
        setCategories([]);
        setTopics([]);
        setProblemCategories([]);
        setHasMore(false);
        setLoadingMore(false);
        loadOptions(currentSession);
        return () => {
            session.current += 1;
            topicRequest.current += 1;
        };
        // 初始选择仅在打开新会话时读取，避免父组件渲染重置已选项。
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [visible, threadId, sourceType]);

    useEffect(() => {
        if (!visible) return;
        const previousFocus = document.activeElement as HTMLElement | null;
        syncModalBodyScrollLock(true, false);
        const keyDown = (event: KeyboardEvent) => {
            if (event.key === 'Escape') {
                event.preventDefault();
                if (!submitting.current) callbacks.current.onClose();
            }
            if (event.key === 'Tab') {
                const elements = Array.from(panel.current?.querySelectorAll<HTMLElement>('button:not(:disabled), input, select, [tabindex="0"]') || []);
                const first = elements[0];
                const last = elements[elements.length - 1];
                if (!first) { event.preventDefault(); panel.current?.focus(); }
                else if (event.shiftKey && (document.activeElement === first || !panel.current?.contains(document.activeElement))) { event.preventDefault(); last.focus(); }
                else if (!event.shiftKey && (document.activeElement === last || !panel.current?.contains(document.activeElement))) { event.preventDefault(); first.focus(); }
            }
        };
        document.addEventListener('keydown', keyDown);
        return () => {
            document.removeEventListener('keydown', keyDown);
            releaseModalBodyScrollLock(true);
            previousFocus?.focus();
        };
    }, [visible]);

    useEffect(() => {
        if (!visible) return;
        const timer = setTimeout(() => {
            // 确认弹窗先聚焦容器，避免自动给“取消”按钮显示键盘焦点描边。
            const focusTarget = step === 'confirm'
                ? panel.current
                : panel.current?.querySelector<HTMLElement>('button:not(:disabled), input');
            focusTarget?.focus();
        }, 50);
        return () => clearTimeout(timer);
    }, [visible, step, loading, error]);

    // 含视频的问题帖确认话题后直接转换，其余场景保留原确认流程。
    const submit = async (problem = selectedProblem, conversionTopics = selectedTopics) => {
        if (submitting.current || (step !== 'confirm' && !(isFeedback && hasVideo && step === 'topics'))) return;
        if ((isFeedback && !conversionTopics.length) || (!isFeedback && !problem)) return;
        const currentSession = session.current;
        const payload: ThreadConversionPayload = {
            threadId,
            sourceType,
            targetType: isFeedback ? 'normal' : 'feedback',
            ...(isFeedback ? { topicIds: conversionTopics.map(topic => topic.id) } : { problemId: problem!.value }),
        };
        submitting.current = true;
        setStep('submitting');
        try {
            await callbacks.current.onConvert(payload);
        } catch {
            if (currentSession === session.current) {
                submitting.current = false;
                // 后端拒绝转换时结束本次流程，不重复弹出确认框或误报成功。
                callbacks.current.onClose();
            }
            return;
        }
        if (currentSession !== session.current) return;
        submitting.current = false;
        callbacks.current.onClose();
        // 转换成功与后续刷新分开处理，刷新异常不改成转换失败。
        Promise.resolve(callbacks.current.onSuccess?.(payload)).catch(() => {});
    };

    if (!visible) return null;
    const choosing = step === 'topics' || step === 'problems';
    const showingTopicPicker = step === 'topics' && !error && (!loading || categories.length > 0);
    return createPortal(
        <div ref={panel} className={styles.flow} role="dialog" aria-modal="true" aria-label={choosing ? (isFeedback ? '选择话题' : '问题类型') : (step === 'submitting' ? '帖子转换中' : confirmText)} tabIndex={-1}>
            {step === 'problems' && !loading && !error ? (
                <PCFeedbackProblemPicker visible categories={problemCategories} value={selectedProblem?.value} secondaryAction="cancel" variant="conversion" requireSelection onClose={dismiss} onConfirm={problem => {
                    if (problem) {
                        setSelectedProblem(problem);
                        setStep('confirm');
                    }
                }} />
            ) : (
                <div className={styles.mask} onClick={step === 'submitting' || showingTopicPicker ? undefined : dismiss}>
                    {showingTopicPicker ? (
                        <div className={styles.topicPanel} onClick={event => event.stopPropagation()}>
                            <TopicPicker visible topicList={topics} categoryList={categories.map(category => category.name)} initialSelected={selectedTopics} initialCategory={activeCategory} onSelectionChange={setSelectedTopics} position={{ top: 0, left: 0 }} topicStore={topicSearch} hasMore={hasMore} isLoadingMore={loadingMore} loading={loading} outsideClickBehavior="close" closeOnSelect={false} onClose={dismiss} onCategoryChange={(name, page = 1) => {
                                const category = categories.find(item => item.name === name);
                                if (category) { setActiveCategory(name); loadTopics(category, page); }
                            }} onSelect={selection => {
                                if (!selection.length) return;
                                setSelectedTopics(selection);
                                // 直接使用本次确认的话题，避免读取尚未更新的选择状态。
                                if (hasVideo) submit(selectedProblem, selection);
                                else setStep('confirm');
                            }} />
                        </div>
                    ) : (
                        <div className={`${styles.card} ${step === 'submitting' || (choosing && loading) ? styles.loadingCard : ''}`} onClick={event => event.stopPropagation()}>
                            {error && choosing ? <>
                                <div className={styles.title}>{error}</div>
                                <div className={styles.buttons}>
                                    <button type="button" className={styles.cancel} onClick={dismiss}>取消</button>
                                    <button type="button" className={styles.confirm} onClick={() => {
                                        if (isFeedback && categories.length) loadTopics(categories.find(item => item.name === activeCategory) || categories[0], 1);
                                        else loadOptions(session.current);
                                    }}>重试</button>
                                </div>
                            </> : step === 'submitting' || (choosing && loading) ? <div className={styles.loading} role="status" aria-live="polite">
                                <img src={threadConversionImage.converting} alt="" className={styles.loadingIcon} />
                                <div className={styles.loadingText}>{step === 'submitting' ? '帖子转换中...' : '正在加载...'}</div>
                            </div> : <>
                                <div className={styles.title}>{confirmText}</div>
                                <div className={styles.buttons}>
                                    <button type="button" className={styles.cancel} onClick={dismiss}>取消</button>
                                    <button type="button" className={styles.confirm} onClick={() => submit()}>确定</button>
                                </div>
                            </>}
                        </div>
                    )}
                </div>
            )}
        </div>, document.body,
    );
};

export default ThreadConversion;
